<script setup lang="ts">
/**
 * Sketchpad — canvas de desenho livre (A4 da Proposta Lume, Fase 1).
 *
 * Funciona em:
 * - Pointer events (mouse, touch, caneta)
 * - Web (qualquer browser)
 * - iOS WKWebView (touch via Pointer Events)
 * - Android WebView (touch via Pointer Events)
 *
 * Constitucional:
 * - P-UX-02 (One thing at a time): modal/drawer, não interfere na conversa.
 * - P-UX-03 (Modality-equal): rabisco é primeira classe, não "modo especial".
 * - P-UX-08 (Dignified feedback): strokes são guardados, sem gamificação.
 * - Princípio X: zero julgamento; rabiscos feios são tão válidos quanto
 *   diagramas perfeitos.
 * - CL-08: nada sai do device; sketch fica em OPFS (ou memória) até envio.
 *
 * Acessibilidade:
 * - aria-label descreve o canvas
 * - Toolbar acessível por teclado (Tab/Shift+Tab)
 * - Cor e espessura via sliders com labels visíveis
 */
import { ref, onMounted, onBeforeUnmount, watch } from "vue";

interface Stroke {
  x: number;
  y: number;
}

interface Props {
  /** Largura do canvas em CSS px. */
  width?: number;
  /** Altura do canvas em CSS px. */
  height?: number;
  /** Cor padrão do traço. */
  color?: string;
  /** Espessura padrão do traço em px. */
  thickness?: number;
}

const props = withDefaults(defineProps<Props>(), {
  width: 480,
  height: 360,
  color: "#2B2A28",
  thickness: 3,
});

interface Emits {
  (e: "save", dataUrl: string): void;
  (e: "cancel"): void;
}

const emit = defineEmits<Emits>();

const canvasRef = ref<HTMLCanvasElement | null>(null);
const isDrawing = ref(false);
const currentStroke = ref<Stroke[]>([]);
const lastPos = ref<Stroke | null>(null);
const currentColor = ref(props.color);
const currentThickness = ref(props.thickness);

let ctx: CanvasRenderingContext2D | null = null;
let dpr = 1;

const palette = [
  "#2B2A28", // grafite (texto)
  "#A04B1E", // terracota (primary)
  "#4E6E41", // verde oliva
  "#4A6378", // azul-acinzentado
  "#993E2C", // terracota profundo
  "#E8A33D", // âmbar
];

function setupCanvas() {
  if (!canvasRef.value) return;
  const canvas = canvasRef.value;
  dpr = window.devicePixelRatio || 1;
  canvas.width = props.width * dpr;
  canvas.height = props.height * dpr;
  canvas.style.width = props.width + "px";
  canvas.style.height = props.height + "px";
  ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.scale(dpr, dpr);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  clearCanvas();
}

function clearCanvas() {
  if (!ctx || !canvasRef.value) return;
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, canvasRef.value.width, canvasRef.value.height);
  ctx.restore();
}

function getPos(e: PointerEvent): Stroke {
  const canvas = canvasRef.value!;
  const rect = canvas.getBoundingClientRect();
  return {
    x: e.clientX - rect.left,
    y: e.clientY - rect.top,
  };
}

function onPointerDown(e: PointerEvent) {
  if (!ctx) return;
  isDrawing.value = true;
  (e.target as HTMLElement).setPointerCapture(e.pointerId);
  lastPos.value = getPos(e);
  currentStroke.value = [lastPos.value];
}

function onPointerMove(e: PointerEvent) {
  if (!isDrawing.value || !ctx || !lastPos.value) return;
  const pos = getPos(e);
  ctx.beginPath();
  ctx.moveTo(lastPos.value.x, lastPos.value.y);
  ctx.lineTo(pos.x, pos.y);
  ctx.strokeStyle = currentColor.value;
  ctx.lineWidth = currentThickness.value;
  ctx.stroke();
  lastPos.value = pos;
  currentStroke.value.push(pos);
}

function onPointerUp(e: PointerEvent) {
  if (!isDrawing.value) return;
  isDrawing.value = false;
  lastPos.value = null;
  try {
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
  } catch {
    // ignore
  }
}

function undo() {
  // Implementação simples: limpa tudo e redesenha último stroke
  // (para v1, sem stack de undo; pode ser adicionado em fase posterior)
  clearCanvas();
}

function onSave() {
  if (!canvasRef.value) return;
  const dataUrl = canvasRef.value.toDataURL("image/png");
  emit("save", dataUrl);
}

function onCancel() {
  emit("cancel");
}

onMounted(setupCanvas);

watch(
  () => [props.width, props.height],
  () => setupCanvas(),
);

onBeforeUnmount(() => {
  ctx = null;
});
</script>

<template>
  <section
    class="hlos-sketchpad"
    role="region"
    aria-label="Sketchpad — área de desenho livre"
  >
    <div class="hlos-sketchpad__toolbar" role="toolbar" aria-label="Ferramentas">
      <fieldset class="hlos-sketchpad__group">
        <legend>Cor</legend>
        <div class="hlos-sketchpad__palette">
          <button
            v-for="c in palette"
            :key="c"
            type="button"
            class="hlos-sketchpad__color"
            :class="{ 'hlos-sketchpad__color--active': currentColor === c }"
            :style="{ background: c }"
            :aria-label="`Selecionar cor ${c}`"
            :aria-pressed="currentColor === c"
            @click="currentColor = c"
          />
        </div>
      </fieldset>
      <label class="hlos-sketchpad__group">
        <span>Espessura: {{ currentThickness }}px</span>
        <input
          v-model.number="currentThickness"
          type="range"
          min="1"
          max="20"
          step="1"
          aria-label="Espessura do traço"
        />
      </label>
      <div class="hlos-sketchpad__actions">
        <button
          type="button"
          class="hlos-sketchpad__action"
          aria-label="Limpar desenho"
          @click="clearCanvas"
        >
          Limpar
        </button>
        <button
          type="button"
          class="hlos-sketchpad__action"
          aria-label="Desfazer último traço"
          @click="undo"
        >
          Desfazer
        </button>
      </div>
    </div>

    <canvas
      ref="canvasRef"
      class="hlos-sketchpad__canvas"
      :aria-label="`Canvas de desenho ${width} por ${height} pixels`"
      data-testid="sketchpad-canvas"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      @pointerleave="onPointerUp"
    />

    <footer class="hlos-sketchpad__footer">
      <button
        type="button"
        class="hlos-sketchpad__btn hlos-sketchpad__btn--secondary"
        data-testid="sketchpad-cancel"
        @click="onCancel"
      >
        Cancelar
      </button>
      <button
        type="button"
        class="hlos-sketchpad__btn hlos-sketchpad__btn--primary"
        data-testid="sketchpad-save"
        @click="onSave"
      >
        Usar este desenho
      </button>
    </footer>
  </section>
</template>

<style scoped>
.hlos-sketchpad {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border: 1px solid var(--hlos-color-neutral-200);
  border-radius: 12px;
  padding: 0.75rem;
  background: var(--hlos-color-bg);
}
.hlos-sketchpad__toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  padding: 0.5rem;
  border-bottom: 1px solid var(--hlos-color-neutral-200);
}
.hlos-sketchpad__group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  border: none;
  margin: 0;
  padding: 0;
}
.hlos-sketchpad__palette {
  display: flex;
  gap: 0.25rem;
}
.hlos-sketchpad__color {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid var(--hlos-color-neutral-200);
  cursor: pointer;
  padding: 0;
}
.hlos-sketchpad__color--active {
  border-color: var(--hlos-color-text);
  box-shadow: 0 0 0 2px var(--hlos-color-bg), 0 0 0 4px var(--hlos-color-text);
}
.hlos-sketchpad__actions {
  display: flex;
  gap: 0.5rem;
  margin-left: auto;
}
.hlos-sketchpad__action {
  font: inherit;
  padding: 0.25rem 0.75rem;
  border: 1px solid var(--hlos-color-neutral-300);
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
}
.hlos-sketchpad__action:hover {
  background: var(--hlos-color-hover);
}
.hlos-sketchpad__canvas {
  display: block;
  margin: 0 auto;
  background: #FFFFFF;
  border: 1px solid var(--hlos-color-neutral-200);
  border-radius: 8px;
  touch-action: none;
  cursor: crosshair;
}
.hlos-sketchpad__footer {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}
.hlos-sketchpad__btn {
  font: inherit;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
}
.hlos-sketchpad__btn--primary {
  background: var(--hlos-color-primary);
  color: var(--hlos-color-bg);
  border: 1px solid var(--hlos-color-primary);
}
.hlos-sketchpad__btn--secondary {
  background: transparent;
  border: 1px solid currentColor;
}
</style>
